import NextLink from 'next/link';
import { Badge } from '@savvy-kit/components/badge';
import { Button } from '@savvy-kit/components/button';
import { Separator } from '@savvy-kit/components/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { PulseRing } from '@savvy-kit/foundations/motion/pulse-ring';
import { SavvyLogo } from '@savvy-kit/foundations/savvy-logo';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@savvy-kit/templates/sidebar';
import { RotateCw } from 'lucide-react';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import type { ClientPortalAccountMenuState, ClientPortalNavId, ClientPortalNavState } from './types';
import { ClientPortalBrandHeader } from './client-portal-brand-header';
import { ClientPortalUserMenu } from './client-portal-user-menu';
import {
  CLIENT_PORTAL_NAV_BUTTON_CLASS,
  CLIENT_PORTAL_NAV_ELEMENT_NAMES,
  CLIENT_PORTAL_NAV_GROUPS,
  CLIENT_PORTAL_NAV_RETRY_ELEMENT_NAME,
  CLIENT_PORTAL_NAV_RETRY_LABEL,
  CLIENT_PORTAL_NAV_RETRY_MESSAGE,
  CLIENT_PORTAL_NAV_UNAVAILABLE_CLASS,
  CLIENT_PORTAL_SIDEBAR_CLASS,
  CLIENT_PORTAL_SIDEBAR_NAV_GROUP_CLASS,
  CLIENT_PORTAL_SIDEBAR_TRIGGER_CLASS,
} from './constants';
import {
  clientPortalNavAvailability,
  clientPortalNavHref,
  clientPortalNavUnavailableLabel,
  formatTaskBadgeCount,
  isPresentString,
} from './utils';

type ClientPortalSidebarProps = {
  readonly activeNavId: ClientPortalNavId | null;
  readonly firmName: string | null;
  readonly firmLogoUrl: string | null;
  readonly brandingLoading: boolean;
  readonly clientName: string;
  readonly clientEmail: string | null;
  readonly clientAvatarUrl: string | null;
  readonly nav: ClientPortalNavState;
  readonly openTaskCount: number;
  readonly navFailed: boolean;
  readonly onRetry: () => void;
  readonly accountMenu: ClientPortalAccountMenuState;
};

export function ClientPortalSidebar({
  activeNavId,
  firmName,
  firmLogoUrl,
  brandingLoading,
  clientName,
  clientEmail,
  clientAvatarUrl,
  nav,
  openTaskCount,
  navFailed,
  onRetry,
  accountMenu,
}: ClientPortalSidebarProps) {
  const trackInteraction = useTrackEvent('userInteraction');
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const isWhiteLabelled = isPresentString(firmName) || isPresentString(firmLogoUrl);
  const isAccountActive = activeNavId === 'account';

  function trackNavClick(navId: ClientPortalNavId) {
    trackInteraction({
      elementName: CLIENT_PORTAL_NAV_ELEMENT_NAMES[navId],
      htmlElement: 'link',
      interactionType: 'button_click',
    });
  }

  return (
    <Sidebar collapsible="icon" className={CLIENT_PORTAL_SIDEBAR_CLASS}>
      <SidebarHeader>
        <div className={cn('flex h-10 items-center gap-2', isCollapsed ? 'justify-center' : 'justify-between pl-2')}>
          {!isCollapsed && (
            <ClientPortalBrandHeader firmName={firmName} firmLogoUrl={firmLogoUrl} isLoading={brandingLoading} />
          )}
          <SidebarTrigger
            aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            className={cn(CLIENT_PORTAL_SIDEBAR_TRIGGER_CLASS, isCollapsed && 'rounded-md border border-border-light')}
          />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {CLIENT_PORTAL_NAV_GROUPS.map((group, groupIndex) => (
          <SidebarGroup key={group.label ?? `group-${groupIndex}`} className={CLIENT_PORTAL_SIDEBAR_NAV_GROUP_CLASS}>
            {isPresentString(group.label) && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeNavId === item.id;
                  const availability = clientPortalNavAvailability(item.id, nav);
                  const href =
                    availability === 'available' ? clientPortalNavHref(item.id, nav.personalPortfolioId) : null;
                  const isUnavailable = href === null;
                  const unavailableLabel = clientPortalNavUnavailableLabel(item.id, availability);
                  const showBadge = item.showBadge === true && openTaskCount > 0 && !isCollapsed;
                  const navButton = (
                    <SidebarMenuButton
                      isActive={isActive}
                      aria-label={isUnavailable ? `${item.label}, ${unavailableLabel}` : item.label}
                      aria-current={isActive ? 'page' : undefined}
                      aria-disabled={isUnavailable ? true : undefined}
                      tooltip={isUnavailable ? undefined : item.label}
                      className={cn(
                        CLIENT_PORTAL_NAV_BUTTON_CLASS,
                        isUnavailable && CLIENT_PORTAL_NAV_UNAVAILABLE_CLASS,
                      )}
                      render={
                        isUnavailable ? undefined : (
                          <NextLink
                            href={href}
                            onClick={() => {
                              trackNavClick(item.id);
                            }}
                          />
                        )
                      }
                    >
                      <Icon size={16} active={isActive} />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  );
                  return (
                    <SidebarMenuItem key={item.id}>
                      {isUnavailable ? (
                        <Tooltip>
                          <TooltipTrigger render={navButton} />
                          <TooltipContent side="right" align="center">
                            {unavailableLabel}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        navButton
                      )}
                      {showBadge && (
                        <SidebarMenuBadge className="h-auto min-w-0 p-0">
                          <span className="relative flex items-center justify-center">
                            <Badge variant="primary" type="circle-sm" className="relative z-10 min-w-5 px-1">
                              {formatTaskBadgeCount(openTaskCount)}
                            </Badge>
                            {!isActive && <PulseRing tone="var(--primary)" urgent />}
                          </span>
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        {navFailed && !isCollapsed && (
          <SidebarGroup>
            <SidebarGroupContent className="px-2">
              <Text variant="caption" className="mb-2 block text-secondary">
                {CLIENT_PORTAL_NAV_RETRY_MESSAGE}
              </Text>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  trackInteraction({
                    elementName: CLIENT_PORTAL_NAV_RETRY_ELEMENT_NAME,
                    htmlElement: 'button',
                    interactionType: 'button_click',
                  });
                  onRetry();
                }}
              >
                <RotateCw size={14} />
                {CLIENT_PORTAL_NAV_RETRY_LABEL}
              </Button>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <ClientPortalUserMenu
          clientName={clientName}
          clientEmail={clientEmail}
          clientAvatarUrl={clientAvatarUrl}
          isAccountActive={isAccountActive}
          accountMenu={accountMenu}
        />
        {isWhiteLabelled && !isCollapsed && (
          <div className="mt-1 px-2">
            <Separator className="mb-3 bg-border-light" />
            <div className="flex items-center gap-1.5">
              <Text variant="caption">Powered by</Text>
              <SavvyLogo size="sm" color="secondary" />
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
